import type { FlowGroup } from './types';

/**
 * Copy and settings for Phase 1 of Alma's two-phase /try chat. The UI renders
 * from this plus the admin-managed lists (destinations, host_specialties,
 * alma_question_branches, alma_question_options) — changing wording here
 * needs no component change.
 *
 * `{persona}` is the active persona's name, `{name}` the traveler's.
 */

/** Saved with the answers so portraits can tell which flow produced them. */
export const FLOW_VERSION = 'two-phase-2026-10c';

/** Typing-dots pause before each scripted Alma line. */
export const SCRIPTED_TYPING_MS = 600;

/** How many of the traveler's top priorities can open a follow-up question. */
export const BRANCH_PRIORITY_LIMIT = 3;

export const HEADCOUNT = { min: 2, max: 12, defaultValue: 2 };

/** Longest name we keep from the name step. */
export const NAME_MAX_LENGTH = 40;

/** Chip label for the built-in free-text option on priorities and branches. */
export const OTHER_LABEL = 'Other';

export const ALMA_LINES = {
  /** Anonymous traveler: asked before anything else, answered in the composer. */
  askName: "Hi, I'm {persona}. What should I call you?",
  /** After the name step. */
  welcomeNamed: "Nice to meet you, {name}. A few taps first, then I'd love to hear how you travel.",
  /** Signed-in traveler (name from the profile). */
  welcomeKnown: "Hi {name}, I'm {persona}. A few taps first, then I'd love to hear how you travel.",
  /** Signed-in traveler without a name on their profile. */
  welcomeUnknown: "Hi, I'm {persona}. A few taps first, then I'd love to hear how you travel.",
  who: 'Good week for it.',
  priorities: 'Forget your usual routine for a second.',
  /** Before walking, when a branch was answered. */
  walkingAfterBranch: 'Good picks. Last quick one.',
  walking: 'Last quick one.',
};

export const COMPOSER_PLACEHOLDER_NAME = 'Your name';

export const STEP_COPY = {
  where_when: { title: 'Where and when', helper: "Pick in the order you'll visit. Days you don't pick, like a travel day, aren't counted." },
  who: { title: "Who's coming", helper: undefined as string | undefined },
  priorities: { title: 'What matters most on this trip', helper: 'Pick all that matter, most important first.' },
  walking: { title: 'How much walking is comfortable' },
};

export const WALKING_HELPER: Record<FlowGroup, string> = {
  solo: "Pick what's comfortable for you.",
  partner: 'Pick what works for both of you.',
  friends: 'Pick what works for everyone in the group.',
  family: 'Pick what works for everyone in the group.',
};

export const GROUP_OPTIONS: { id: FlowGroup; label: string }[] = [
  { id: 'solo', label: 'Just me' },
  { id: 'partner', label: 'With a partner' },
  { id: 'friends', label: 'With friends' },
  { id: 'family', label: 'With family' },
];

export const HEADCOUNT_LABEL = 'How many of you · including you';

/** Plain-words validation, shown under the date that's wrong. */
export const DATE_ERRORS = {
  past: 'Pick a date after today.',
  /** {date} is the previous date, e.g. "Oct 14". Days between two cities may stay empty. */
  firstDayOrder: "Pick a date after {date}. Cities can't overlap.",
  lastDayOrder: 'Pick a date on or after {date}.',
};

/** Added to a city that has too few hosts to be booked yet. */
export const COMING_SOON = ' · coming soon';

export const LIST_ERROR = "Couldn't load the options. Try again.";

/** Fills `{key}` placeholders. */
export function fill(template: string, values: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (m, key: string) => values[key] ?? m);
}
